import { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Ruler, MapPin, AlertTriangle, Loader2, Download, FileText, Pencil, X, Home, Droplet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { AddressAutocomplete } from "@/components/measurements/AddressAutocomplete";
import { RoofFacetCard } from "@/components/measurements/RoofFacetCard";
import { AIVisionUpload } from "@/components/measurements/AIVisionUpload";
import { SaveToJobDialog } from "@/components/measurements/SaveToJobDialog";
import { MeasurementHistory } from "@/components/measurements/MeasurementHistory";
import { getStaticMapUrl } from "@/utils/roofCalculations";
import { analyzeRoofGeometry } from "@/utils/roofGeometryAnalyzer";
import { calculateWaste, getAveragePitchRatio } from "@/utils/roofWasteCalculations";
import { KeyMetricsCard } from "@/components/measurements/KeyMetricsCard";
import { RoofVisualizationTabs } from "@/components/measurements/RoofVisualizationTabs";
import { useRoofGeometry } from "@/hooks/useRoofGeometry";
import html2pdf from "html2pdf.js";
import { SidingEstimate } from "@/components/measurements/SidingEstimate";
import { GuttersEstimate } from "@/components/measurements/GuttersEstimate";
import { QuickPriceEstimate } from "@/components/measurements/QuickPriceEstimate";
import { ManualRoofTrace } from "@/components/measurements/ManualRoofTrace";
import { MeasurementActionHub } from "@/components/measurements/MeasurementActionHub";
import { RiskAssessment } from "@/components/measurements/RiskAssessment";
import { WasteFactorPicker } from "@/components/measurements/WasteFactorPicker";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";

interface RoofVertex {
  lat: number;
  lng: number;
}

interface RoofPlane {
  id: string;
  areaSqFt: number;
  tiltDegrees: number | null;
  azimuthDegrees: number | null;
  boundingBox?: {
    sw: { lat: number; lng: number };
    ne: { lat: number; lng: number };
  };
  center?: {
    lat: number;
    lng: number;
  };
  vertices: RoofVertex[];
}

interface BuildingData {
  facadeAreaSqFt?: number;
  soffitAreaSqFt?: number;
  fasciaLengthFt?: number;
  gutterLengthFt?: number;
  downspoutCount?: number;
}

interface MeasurementData {
  lat: number;
  lng: number;
  address: string;
  totalAreaSqFt: number;
  totalSquares: number;
  planes: RoofPlane[];
  buildingData?: BuildingData;
}

export default function Measurements() {
  const location = useLocation();
  const { user } = useAuth();
  const [measurementType, setMeasurementType] = useState<"roof" | "siding" | "gutters" | "history">("roof");
  const [address, setAddress] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MeasurementData | null>(null);
  const [aiNotes, setAiNotes] = useState<any[]>([]);
  const [visionResults, setVisionResults] = useState<any>(null);
  const { isLoaded, loadError } = useGoogleMaps();
  const [lastSelectedAddress, setLastSelectedAddress] = useState<string>("");
  const [roofEdgeData, setRoofEdgeData] = useState<any>(null);
  const [wasteFactors, setWasteFactors] = useState<any>(null);
  const [complexityScore, setComplexityScore] = useState<number>(1);
  const [wastePercent, setWastePercent] = useState<number>(10);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [fullAutoMeasureLoading, setFullAutoMeasureLoading] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [estimateData, setEstimateData] = useState<any>(null);
  const [manualTraceMode, setManualTraceMode] = useState(false);
  const [manualMeasurement, setManualMeasurement] = useState<MeasurementData | null>(null);

  // Handler for manual trace completion
  const handleManualTraceComplete = useCallback((measurement: MeasurementData) => {
    setManualMeasurement(measurement);
    setData(measurement);
    setManualTraceMode(false);
    toast.success("Manual measurement completed!");
  }, []);

  // Pre-fill address from navigation state
  useEffect(() => {
    if (location.state?.address) {
      setAddress(location.state.address);
    }
  }, [location.state]);

  // Transform plane data to RoofGeometry for visualization
  const roofGeometry = useRoofGeometry(data?.planes || []);

  const handlePlaceSelected = useCallback((place: { address: string; lat: number; lng: number }) => {
    console.log("✅ Place selected:", place.address);
    setAddress(place.address);
    setSelectedLocation({ lat: place.lat, lng: place.lng });
    setLastSelectedAddress(place.address);
    setError(null);
  }, []);

  const handleAddressChange = useCallback((value: string) => {
    console.log("📝 Address change:", value);
    setAddress(value);
    setSelectedLocation(null);
  }, []);

  const handleFullAutoMeasure = async () => {
    if (!address.trim()) {
      toast.error("Please enter an address");
      return;
    }

    if (!selectedLocation) {
      toast.error("Please select a valid address from the suggestions");
      return;
    }

    setFullAutoMeasureLoading(true);
    await handleRunMeasurement();
    setFullAutoMeasureLoading(false);
    
    // Switch to Measurement History tab after measurements complete
    setTimeout(() => {
      setMeasurementType("history");
      toast.success("Full property measured! View measurement history below.");
    }, 500);
  };

  const handleRunMeasurement = async () => {
    if (!address.trim()) {
      toast.error("Please enter an address");
      return;
    }

    // If Maps autocomplete didn’t run, still allow using the raw address
    if (!selectedLocation) {
      console.warn("Using address without map-selected location for measurement");
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const { data: responseData, error: functionError } = await supabase.functions.invoke(
        "google-solar-measurement",
        {
          body: { address },
        }
      );

      // Handle function errors - extract actual error message
      if (functionError) {
        console.error("Edge function error:", functionError);
        
        // Try to parse the actual error from the response
        let errorMsg = "Failed to fetch roof measurements";
        
        // Check if error message contains JSON with actual error
        try {
          if (functionError.message && functionError.message.includes("{")) {
            const parsed = JSON.parse(functionError.message);
            errorMsg = parsed.error || errorMsg;
          } else if (functionError.message) {
            errorMsg = functionError.message;
          }
        } catch {
          errorMsg = functionError.message || errorMsg;
        }
        
        setError(errorMsg);
        toast.error(errorMsg, {
          description: "Try a different address or check your API key configuration"
        });
        return;
      }

      if (responseData?.error) {
        const errorMsg = responseData.error;
        setError(errorMsg);
        toast.error(errorMsg, {
          description: "Try a different address or use Manual Trace for measurement"
        });
        return;
      }

      // Transform the response to match expected format
      const measurement = responseData.measurement;
      const lat = selectedLocation?.lat ?? measurement?.lat ?? 0;
      const lng = selectedLocation?.lng ?? measurement?.lng ?? 0;
      const transformedData = {
        lat,
        lng,
        address: measurement.address,
        totalAreaSqFt: measurement.total_area,
        totalSquares: measurement.total_squares,
        planes: measurement.facets || [],
      };

      setData(transformedData);
      
      // Set the roofEdgeData state directly from the Solar API response
      setRoofEdgeData({
        totals: {
          ridgeLengthFt: measurement.ridges,
          hipLengthFt: measurement.hips,
          valleyLengthFt: measurement.valleys,
          eaveLengthFt: measurement.eaves,
          rakeLengthFt: measurement.rakes,
          perimeterFt: (measurement.ridges + measurement.hips + measurement.valleys + measurement.eaves + measurement.rakes),
        },
        perimeter: (measurement.ridges + measurement.hips + measurement.valleys + measurement.eaves + measurement.rakes),
        totalEaveLength: measurement.eaves,
        totalRakeLength: measurement.rakes,
        totalRidgeLength: measurement.ridges,
        totalHipLength: measurement.hips,
        totalValleyLength: measurement.valleys,
      });
      
      // Calculate waste factors
      const avgPitch = transformedData.planes.length > 0 ? getAveragePitchRatio(transformedData.planes) : 0;
      const waste = calculateWaste(measurement.total_squares, avgPitch, transformedData.planes.length);
      setWasteFactors(waste);
      
      // Calculate complexity score (1-5)
      const planeCount = transformedData.planes.length;
      const complexity = 
        planeCount <= 3 ? 1 :
        planeCount <= 6 ? 2 :
        planeCount <= 9 ? 3 :
        planeCount <= 12 ? 4 : 5;
      setComplexityScore(complexity);
      
      // Set initial waste percent based on complexity
      const aiSuggestedWaste = complexity <= 2 ? 10 : complexity <= 3 ? 15 : 20;
      setWastePercent(aiSuggestedWaste);
      
      // Generate AI notes based on measurement data
      const notes = [
        { type: "Material Estimate", content: "Based on location and roof age, likely asphalt composition shingles" },
        { type: "Pitch Analysis", content: avgPitch > 0 ? `Average pitch: ${avgPitch}/12` : "Pitch data unavailable" },
        { type: "Features", content: `${transformedData.planes.length} distinct roof planes identified` },
        { type: "Waste Factor", content: `${waste.wastePercent}% recommended (${waste.cutUpFactor} complexity)` },
        { type: "Linear Measurements", content: `Ridges: ${measurement.ridges}ft, Eaves: ${measurement.eaves}ft, Rakes: ${measurement.rakes}ft` },
      ];
      setAiNotes(notes);
      
      toast.success("Measurement completed successfully!");
      setRefreshHistory(prev => prev + 1); // Trigger history refresh

      // Note: Roof measurement already saved by google-solar-measurement function
      console.log("✅ Roof measurement saved successfully");
    } catch (err: any) {
      console.error("Measurement error:", err);
      const errorMessage = err.message || "Failed to fetch roof measurements";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateMaterials = (squares: number) => {
    const shingleBundles = Math.ceil(squares * 3);
    const underlaymentRolls = Math.ceil(squares / 4);
    const ridgeCap = Math.ceil(squares * 0.6);
    return { shingleBundles, underlaymentRolls, ridgeCap };
  };

  const handleDownloadPDF = async () => {
    if (!data) return;
    
    setPdfGenerating(true);
    toast.loading("Generating PDF...");

    try {
      // Get pricing data if available
      let pricingEstimate = null;
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data: pricingSettings } = await supabase
          .from('pricing_settings')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (pricingSettings) {
          // Calculate estimate (assuming asphalt with tearoff as default)
          const avgPitchStr = String(getAveragePitchRatio(data.planes));
          const pitchRise = parseInt(avgPitchStr.split('/')[0]) || 0;
          const isSteepPitch = pitchRise >= 8;
          const totalWastePercent = pricingSettings.roof_base_waste_percent + 
            (isSteepPitch ? pricingSettings.roof_steep_pitch_extra_percent : 0);
          const squaresWithWaste = data.totalSquares * (1 + totalWastePercent / 100);
          
          const baseRoofPrice = squaresWithWaste * pricingSettings.roof_price_per_sq_asphalt;
          const tearoffCost = squaresWithWaste * pricingSettings.roof_tearoff_per_sq;
          const dumpCost = squaresWithWaste * pricingSettings.roof_dump_fee_per_sq;
          const subtotalCost = baseRoofPrice + tearoffCost + dumpCost;
          const overheadCost = subtotalCost * (pricingSettings.overhead_percent / 100);
          const costWithOverhead = subtotalCost + overheadCost;
          const profitAmount = costWithOverhead * (pricingSettings.profit_margin_percent / 100);
          const finalPrice = costWithOverhead + profitAmount;

          pricingEstimate = {
            material: 'Asphalt Shingles',
            includeTearoff: true,
            subtotalCost,
            overheadCost,
            profitAmount,
            finalPrice,
          };
        }
      }

      // Prepare measurement data for PDF
      const measurementData = {
        address: data.address,
        lat: data.lat,
        lng: data.lng,
        totalAreaSqFt: data.totalAreaSqFt,
        totalSquares: data.totalSquares,
        planes: data.planes.map((plane: any, idx: number) => ({
          ...plane,
          pitchRatio: roofGeometry.planes[idx]?.pitchRatio,
          orientationLabel: roofGeometry.planes[idx]?.orientationLabel,
        })),
        roofEdgeData,
        wasteFactors,
        avgPitch: `${getAveragePitchRatio(data.planes)}/12`,
        complexityScore,
        pricingEstimate,
      };

      // Call edge function to generate PDF HTML
      const { data: pdfData, error } = await supabase.functions.invoke(
        'generate-measurement-pdf',
        {
          body: { measurementData },
        }
      );

      if (error) throw error;

      // Convert HTML to PDF using html2pdf
      const element = document.createElement('div');
      element.innerHTML = pdfData.html;
      
      const opt = {
        margin: 0,
        filename: pdfData.filename,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
      };

      await html2pdf().set(opt).from(element).save();
      
      toast.dismiss();
      toast.success("PDF downloaded successfully!");
    } catch (err: any) {
      console.error("PDF generation error:", err);
      toast.dismiss();
      toast.error("Failed to generate PDF: " + (err.message || "Unknown error"));
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header with Title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Measurement Genius V2</h1>
          <p className="text-muted-foreground mt-1">
            Get instant measurements and estimates for roofing, siding, and gutters.
          </p>
        </div>
        {/* Sentry Test Button - Remove after testing */}
      </div>

      {/* Full Exterior Auto-Measure Button */}
      {selectedLocation && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">⚡ One-Click Full Property Measurement</h3>
                <p className="text-sm text-muted-foreground">
                  Automatically measure roof, siding, and gutters all at once
                </p>
              </div>
              <Button 
                onClick={handleFullAutoMeasure}
                disabled={fullAutoMeasureLoading || loading}
                size="lg"
                className="gap-2"
              >
                {fullAutoMeasureLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Measuring...
                  </>
                ) : (
                  <>
                    <Ruler className="w-4 h-4" />
                    Auto-Measure Whole Property
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Measurement Type Selection - Blue Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={measurementType === "roof" ? "default" : "outline"}
          onClick={() => setMeasurementType("roof")}
          className="gap-2"
        >
          <Ruler className="w-4 h-4" />
          Roof
        </Button>
        <Button
          variant={measurementType === "siding" ? "default" : "outline"}
          onClick={() => setMeasurementType("siding")}
          className="gap-2"
        >
          <Home className="w-4 h-4" />
          Siding
        </Button>
        <Button
          variant={measurementType === "gutters" ? "default" : "outline"}
          onClick={() => setMeasurementType("gutters")}
          className="gap-2"
        >
          <Droplet className="w-4 h-4" />
          Gutters
        </Button>
        <Button
          variant={measurementType === "history" ? "default" : "outline"}
          onClick={() => setMeasurementType("history")}
          className="gap-2"
        >
          <FileText className="w-4 h-4" />
          Measurement History
        </Button>
      </div>

      {/* Roof Measurement Content */}
      {measurementType === "roof" && (
        <div className="space-y-6">
          {/* Manual Trace Mode */}
          {manualTraceMode && selectedLocation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Manual Roof Trace - Click to Draw Polygons</h2>
                <Button variant="outline" size="sm" onClick={() => setManualTraceMode(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Exit Manual Mode
                </Button>
              </div>
              <ManualRoofTrace
                address={address}
                lat={selectedLocation.lat}
                lng={selectedLocation.lng}
                onMeasurementComplete={handleManualTraceComplete}
              />
            </div>
          )}

          {/* New Card-Based Layout */}
          {!manualTraceMode && (
            <div className="space-y-6">
              {/* Address Search Section */}
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <CardTitle>Property Address</CardTitle>
                  </div>
                  <CardDescription>Enter an address to get instant AI-powered measurements</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {loadError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription>{loadError}</AlertDescription>
                      </Alert>
                    )}
                    
                    <AddressAutocomplete
                      value={address}
                      onChange={handleAddressChange}
                      onPlaceSelected={handlePlaceSelected}
                      disabled={loading}
                      isLoaded={isLoaded}
                    />
                    
                    <div className="flex gap-3">
                      <Button 
                        className="bg-action hover:bg-action-hover flex-1"
                        onClick={handleRunMeasurement}
                        disabled={loading}
                        size="lg"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Measuring...
                          </>
                        ) : (
                          <>
                            <Search className="w-5 h-5 mr-2" />
                            AI Measure
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        size="lg"
                        onClick={() => setManualTraceMode(true)}
                        disabled={!selectedLocation}
                      >
                        <Pencil className="w-5 h-5 mr-2" />
                        Manual Trace
                      </Button>
                    </div>

                    {error && (
                      <Alert variant="destructive">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Main Content - Three Column Layout */}
              {data && (
                <>
                  {/* Top Row - Satellite Image and Key Metrics */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Satellite Image */}
                    <div className="lg:col-span-2">
                      <Card>
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">Satellite Imagery</CardTitle>
                            <Badge variant="outline">Satellite Measured</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                          <div className="aspect-video rounded-lg overflow-hidden border bg-muted relative">
                            <img
                              src={getStaticMapUrl(data.lat, data.lng, 19, "800x600")}
                              alt="Satellite view"
                              className="w-full h-full object-cover"
                            />
                            {/* Property Pin Marker */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full z-10">
                              <div className="relative">
                                <MapPin className="w-8 h-8 text-primary drop-shadow-lg" fill="currentColor" />
                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary/30 rounded-full blur-sm" />
                              </div>
                            </div>
                          </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-muted-foreground">
                                Image Date: Recent satellite imagery
                              </p>
                              <Button variant="outline" size="sm" className="gap-2">
                                <MapPin className="w-4 h-4" />
                                Push to Drone App
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Key Metrics Card */}
                    <KeyMetricsCard
                      totalAreaSqFt={data.totalAreaSqFt}
                      totalSquares={data.totalSquares}
                      wasteSquares={(data.totalSquares * (wastePercent / 100))}
                      avgPitch={`${getAveragePitchRatio(data.planes)}/12`}
                      complexityScore={
                        complexityScore <= 2 ? "Simple" : 
                        complexityScore <= 3 ? "Moderate" : "High"
                      }
                      facetCount={data.planes.length}
                    />
                  </div>

                  {/* Second Row - Material Estimate & Linear Measurements */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Material Estimate & Waste */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded bg-action/10">
                            <FileText className="w-4 h-4 text-action" />
                          </div>
                          <CardTitle className="text-base">Material Estimate & Waste</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Materials Needed
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Shingle Bundles</span>
                              <Badge variant="secondary">{calculateMaterials(data.totalSquares * (1 + wastePercent / 100)).shingleBundles} bundles</Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Underlayment</span>
                              <Badge variant="secondary">{calculateMaterials(data.totalSquares * (1 + wastePercent / 100)).underlaymentRolls} rolls</Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Ridge Cap</span>
                              <Badge variant="secondary">{calculateMaterials(data.totalSquares * (1 + wastePercent / 100)).ridgeCap} bundles</Badge>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-warning" />
                            Waste Factors
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Base Squares</span>
                              <span className="font-medium">{data.totalSquares.toFixed(0)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Recommended Waste</span>
                              <Badge variant="secondary" className="text-xs">
                                {wastePercent}%
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Cut-Up Factor</span>
                              <Badge variant="outline" className="text-xs">
                                {complexityScore <= 2 ? "Low" : complexityScore <= 3 ? "Medium" : "High"}
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Waste Squares</span>
                              <span className="font-medium">{(data.totalSquares * (wastePercent / 100)).toFixed(0)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t">
                              <span className="font-semibold">Total w/ Waste</span>
                              <Badge className="bg-warning text-warning-foreground">{(data.totalSquares * (1 + wastePercent / 100)).toFixed(0)} sq</Badge>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-3">
                            * Estimates based on industry averages. Actual needs may vary.
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Linear Measurements */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded bg-action/10">
                            <Ruler className="w-4 h-4 text-action" />
                          </div>
                          <CardTitle className="text-base">Linear Measurements</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between items-center pb-2 border-b">
                          <span className="text-sm font-semibold">Total Perimeter</span>
                          <Badge variant="secondary" className="bg-action text-action-foreground">
                            {roofEdgeData?.perimeter?.toFixed(1) || "0.0"} ft
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              <span className="text-muted-foreground">Ridges</span>
                              <Badge variant="outline" className="text-xs">Estimated</Badge>
                            </div>
                            <span className="font-medium">{roofEdgeData?.totalRidgeLength?.toFixed(1) || "0.0"} ft</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                              <span className="text-muted-foreground">Hips</span>
                              <Badge variant="outline" className="text-xs">Estimated</Badge>
                            </div>
                            <span className="font-medium">{roofEdgeData?.totalHipLength?.toFixed(1) || "0.0"} ft</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-gray-500" />
                              <span className="text-muted-foreground">Valleys</span>
                              <Badge variant="outline" className="text-xs">N/A</Badge>
                            </div>
                            <span className="font-medium">{roofEdgeData?.totalValleyLength?.toFixed(1) || "0.0"} ft</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              <span className="text-muted-foreground">Eaves</span>
                              <Badge variant="outline" className="text-xs">Estimated</Badge>
                            </div>
                            <span className="font-medium">{roofEdgeData?.totalEaveLength?.toFixed(1) || "0.0"} ft</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-orange-500" />
                              <span className="text-muted-foreground">Rakes</span>
                              <Badge variant="outline" className="text-xs">Estimated</Badge>
                            </div>
                            <span className="font-medium">{roofEdgeData?.totalRakeLength?.toFixed(1) || "0.0"} ft</span>
                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Area</span>
                            <Badge variant="secondary" className="bg-action text-action-foreground">
                              {data.totalAreaSqFt.toFixed(3)} ft²
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Squares</span>
                            <Badge variant="secondary" className="bg-action text-action-foreground">
                              {data.totalSquares.toFixed(0)}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Third Row - Roof Facets */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded bg-action/10">
                          <Ruler className="w-4 h-4 text-action" />
                        </div>
                        <div>
                          <CardTitle className="text-base">Roof Facets ({data.planes.length})</CardTitle>
                          <CardDescription className="text-xs">Individual roof segments with pitch and orientation</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.planes.map((plane, idx) => (
                          <RoofFacetCard
                            key={plane.id}
                            facetNumber={idx + 1}
                            areaSqFt={plane.areaSqFt}
                            tiltDegrees={plane.tiltDegrees}
                            azimuthDegrees={plane.azimuthDegrees}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Fourth Row - Quick Price Estimate & AI Notes */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <QuickPriceEstimate
                      totalSquares={data.totalSquares}
                      avgPitchRatio={wasteFactors?.averagePitch || `${getAveragePitchRatio(data.planes)}/12`}
                      onEstimateChange={setEstimateData}
                    />

                    <Card>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded bg-action/10">
                            <AlertTriangle className="w-4 h-4 text-action" />
                          </div>
                          <CardTitle className="text-base">AI Notes</CardTitle>
                        </div>
                        <CardDescription className="text-xs">Auto-generated insights from Solar API measurement data</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {aiNotes.map((note, idx) => (
                          <div key={idx}>
                            <h4 className="text-sm font-semibold mb-1">{note.type}</h4>
                            <p className="text-sm text-muted-foreground">{note.content}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Actions */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Actions</CardTitle>
                      <CardDescription className="text-xs">Export and save measurement data</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button onClick={handleDownloadPDF} disabled={pdfGenerating} className="w-full gap-2" variant="outline" size="lg">
                        {pdfGenerating ? (
                          <>
                            <Download className="w-4 h-4 animate-spin" />
                            Generating PDF...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4" />
                            Download PDF Report
                          </>
                        )}
                      </Button>
                      
                      <SaveToJobDialog
                        measurementData={{
                          ...data,
                          roofEdgeData: roofEdgeData,
                          wasteFactors: wasteFactors
                        }}
                        estimateData={estimateData}
                      />
                    </CardContent>
                  </Card>
                </>
              )}

            </div>
          )}
        </div>
      )}

      {/* Siding Measurement Content */}
      {measurementType === "siding" && (
        <div className="space-y-6 mt-6">
          <SidingEstimate 
            buildingData={data?.buildingData || null}
          />
        </div>
      )}

      {/* Gutters Measurement Content */}
      {measurementType === "gutters" && (
        <div className="space-y-6 mt-6">
          <GuttersEstimate 
            buildingData={data?.buildingData || null}
          />
        </div>
      )}

      {/* Measurement History Content */}
      {measurementType === "history" && (
        <div className="space-y-6 mt-6">
          {/* Measurement History - Always Visible */}
          <MeasurementHistory 
            key={refreshHistory} 
            onAddressClick={(address, type) => {
              setAddress(address);
              setMeasurementType(type);
            }}
          />

          {!data ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Ruler className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold text-lg mb-2">No Current Measurement</h3>
                <p className="text-muted-foreground">
                  Run a property measurement to see live results
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Overview */}
              <div className="grid gap-6 md:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-muted-foreground">Roof Area</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{data.totalAreaSqFt.toFixed(0)} ft²</p>
                    <p className="text-sm text-muted-foreground mt-1">{data.totalSquares.toFixed(1)} squares</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-muted-foreground">Roof Edges</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {roofEdgeData?.perimeter != null ? (
                      <>
                        <p className="text-3xl font-bold">{roofEdgeData.perimeter.toFixed(0)} ft</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {roofEdgeData.totalEaveLength != null ? `${roofEdgeData.totalEaveLength.toFixed(0)} ft eaves` : 'Calculating...'}
                        </p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Not calculated</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-muted-foreground">Property</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-medium">{address}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.lat.toFixed(6)}, {data.lng.toFixed(6)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-primary/20">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10">
                          <FileText className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">Add Measurements to Job</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Save all roof, siding, and gutter measurements to an existing job
                          </p>
                          <SaveToJobDialog
                            measurementData={{
                              ...data,
                              roofEdgeData: roofEdgeData,
                              wasteFactors: wasteFactors
                            }}
                            estimateData={estimateData}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary/20">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10">
                          <Download className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">Download Exterior PDF</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Generate a comprehensive PDF with all measurements and satellite imagery
                          </p>
                          <Button 
                            onClick={handleDownloadPDF} 
                            className="w-full gap-2"
                            disabled={pdfGenerating}
                          >
                            {pdfGenerating ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Generating PDF...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4" />
                                Download Full Report
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Reference */}
              <Card>
                <CardHeader>
                  <CardTitle>Whole Property Measurement Summary</CardTitle>
                  <CardDescription>Complete measurements for roof, siding, and gutters</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6">
                    {/* Roof Section */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <h4 className="font-semibold mb-3 text-primary">Roof Details</h4>
                        <ul className="space-y-2 text-sm">
                          <li className="flex justify-between">
                            <span className="text-muted-foreground">Total Area:</span>
                            <span className="font-medium">{data.totalAreaSqFt.toFixed(0)} ft²</span>
                          </li>
                          <li className="flex justify-between">
                            <span className="text-muted-foreground">Roof Squares:</span>
                            <span className="font-medium">{data.totalSquares.toFixed(1)} sq</span>
                          </li>
                          <li className="flex justify-between">
                            <span className="text-muted-foreground">Roof Planes:</span>
                            <span className="font-medium">{data.planes.length}</span>
                          </li>
                          {wasteFactors?.totalWastePercent != null && (
                            <li className="flex justify-between">
                              <span className="text-muted-foreground">Waste Factor:</span>
                              <span className="font-medium">{wasteFactors.totalWastePercent.toFixed(1)}%</span>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3 text-primary">Edge Measurements</h4>
                        {roofEdgeData?.perimeter != null ? (
                          <ul className="space-y-2 text-sm">
                            <li className="flex justify-between">
                              <span className="text-muted-foreground">Total Perimeter:</span>
                              <span className="font-medium">{roofEdgeData.perimeter.toFixed(0)} ft</span>
                            </li>
                            {roofEdgeData.totalEaveLength != null && (
                              <li className="flex justify-between">
                                <span className="text-muted-foreground">Eave Length:</span>
                                <span className="font-medium">{roofEdgeData.totalEaveLength.toFixed(0)} ft</span>
                              </li>
                            )}
                            {roofEdgeData.totalRakeLength != null && (
                              <li className="flex justify-between">
                                <span className="text-muted-foreground">Rake Length:</span>
                                <span className="font-medium">{roofEdgeData.totalRakeLength.toFixed(0)} ft</span>
                              </li>
                            )}
                            {roofEdgeData.totalRidgeLength != null && (
                              <li className="flex justify-between">
                                <span className="text-muted-foreground">Ridge Length:</span>
                                <span className="font-medium">{roofEdgeData.totalRidgeLength.toFixed(0)} ft</span>
                              </li>
                            )}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">Edge data not available</p>
                        )}
                      </div>
                    </div>

                    {/* Siding & Gutters Section */}
                    {data.buildingData && (
                      <>
                        <div className="border-t pt-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <h4 className="font-semibold mb-3 text-primary">Siding & Trim</h4>
                              <ul className="space-y-2 text-sm">
                                {data.buildingData.facadeAreaSqFt && (
                                  <li className="flex justify-between">
                                    <span className="text-muted-foreground">Facade Area:</span>
                                    <span className="font-medium">{data.buildingData.facadeAreaSqFt.toFixed(0)} ft²</span>
                                  </li>
                                )}
                                {data.buildingData.soffitAreaSqFt && (
                                  <li className="flex justify-between">
                                    <span className="text-muted-foreground">Soffit Area:</span>
                                    <span className="font-medium">{data.buildingData.soffitAreaSqFt.toFixed(0)} ft²</span>
                                  </li>
                                )}
                                {data.buildingData.fasciaLengthFt && (
                                  <li className="flex justify-between">
                                    <span className="text-muted-foreground">Fascia Length:</span>
                                    <span className="font-medium">{data.buildingData.fasciaLengthFt.toFixed(0)} ft</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-3 text-primary">Gutters</h4>
                              <ul className="space-y-2 text-sm">
                                {data.buildingData.gutterLengthFt && (
                                  <li className="flex justify-between">
                                    <span className="text-muted-foreground">Gutter Length:</span>
                                    <span className="font-medium">{data.buildingData.gutterLengthFt.toFixed(0)} ft</span>
                                  </li>
                                )}
                                {data.buildingData.downspoutCount && (
                                  <li className="flex justify-between">
                                    <span className="text-muted-foreground">Downspouts:</span>
                                    <span className="font-medium">{data.buildingData.downspoutCount}</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
